import { createFileRoute } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useEffect, useMemo, useState } from "react";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Package as PackageIcon,
  Plus,
  Pencil,
  Archive,
  ArchiveRestore,
  Minus,
  Settings2,
  AlertCircle,
  MoreHorizontal,
  CalendarPlus,
  CircleSlash,
  Repeat2,
  HelpCircle,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { PackagesConceptCarousel } from "@/components/packages/PackagesConceptCarousel";
import { PackagesEmptyState } from "@/components/packages/PackagesEmptyState";
import { BulkConversionWizard } from "@/components/packages/BulkConversionWizard";

export const Route = createFileRoute("/app/packages")({
  head: () => ({
    meta: buildAppMeta({
      title: "Packages — CoachQuick",
      description: "Sell lesson packs, track client balances, and manage credits.",
      path: "/app/packages",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/packages` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <PackagesPage />
    </RequireAuth>
  ),
});

type Template = {
  id: string;
  name: string;
  description: string | null;
  lessons_count: number;
  price_cents: number;
  expiry_days: number | null;
  low_balance_threshold: number | null;
  active: boolean;
};

type Client = { id: string; student_name: string };

type ClientPackage = {
  id: string;
  coach_student_id: string;
  template_id: string | null;
  status: "pending_payment" | "active" | "expired" | "refunded" | "void";
  lessons_total: number;
  lessons_remaining: number;
  price_cents: number;
  rate_at_purchase_cents: number;
  expires_at: string | null;
  activated_at: string | null;
  opened_without_payment_at: string | null;
  created_at: string;
  notes: string | null;
};

const STATUS_VARIANT: Record<ClientPackage["status"], "default" | "secondary" | "outline" | "destructive"> = {
  active: "default",
  pending_payment: "secondary",
  expired: "outline",
  refunded: "outline",
  void: "destructive",
};

const STATUS_LABEL: Record<ClientPackage["status"], string> = {
  active: "Active",
  pending_payment: "Awaiting payment",
  expired: "Expired",
  refunded: "Refunded",
  void: "Void",
};

function dollars(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function PackagesPage() {
  const { user } = useAuth();
  const [packagesEnabled, setPackagesEnabled] = useState<boolean | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [packs, setPacks] = useState<ClientPackage[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [onboardingDone, setOnboardingDone] = useState<string | null>(null);
  const [bulkDone, setBulkDone] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);



  const [tplDialog, setTplDialog] = useState<{ open: boolean; editing?: Template }>({ open: false });
  const [sellDialog, setSellDialog] = useState(false);
  const [adjustDialog, setAdjustDialog] = useState<ClientPackage | null>(null);
  const [extendDialog, setExtendDialog] = useState<ClientPackage | null>(null);
  const [expireDialog, setExpireDialog] = useState<ClientPackage | null>(null);

  const clientById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const tplById = useMemo(() => Object.fromEntries(templates.map((t) => [t.id, t])), [templates]);

  async function loadAll() {
    if (!user) return;
    setLoading(true);
    const [profileRes, tplRes, packRes, clientRes] = await Promise.all([
      supabase
        .from("coach_profile")
        .select("packages_enabled, packages_onboarding_completed_at, bulk_conversion_completed_at")
        .eq("coach_id", user.id)
        .maybeSingle(),
      supabase
        .from("package_templates")
        .select("*")
        .eq("coach_id", user.id)
        .order("created_at", { ascending: false }),
      supabase
        .from("client_packages")
        .select("*")
        .eq("coach_id", user.id)
        .order("created_at", { ascending: false }),
      supabase
        .from("coach_students")
        .select("id, student_name")
        .eq("coach_id", user.id)
        .order("student_name"),
    ]);
    setPackagesEnabled(profileRes.data?.packages_enabled ?? false);
    setOnboardingDone(profileRes.data?.packages_onboarding_completed_at ?? null);
    setBulkDone(profileRes.data?.bulk_conversion_completed_at ?? null);
    setTemplates((tplRes.data ?? []) as Template[]);
    setPacks((packRes.data ?? []) as ClientPackage[]);
    setClients((clientRes.data ?? []) as Client[]);
    setLoading(false);
  }

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  async function enablePackages() {
    if (!user) return;
    // upsert; trigger seeds starter templates
    const { error } = await supabase
      .from("coach_profile")
      .upsert({ coach_id: user.id, packages_enabled: true }, { onConflict: "coach_id" });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Packages enabled. Three starter templates were added.");
    await loadAll();
  }

  async function toggleArchive(t: Template) {
    const { error } = await supabase
      .from("package_templates")
      .update({ active: !t.active })
      .eq("id", t.id);
    if (error) return toast.error(error.message);
    toast.success(t.active ? "Template archived." : "Template restored.");
    await loadAll();
  }

  if (loading) {
    return (
      <AppShell>
        <Card className="p-6">Loading…</Card>
      </AppShell>
    );
  }

  if (!packagesEnabled) {
    return (
      <AppShell>
        <Card className="p-8 max-w-2xl mx-auto text-center space-y-4">
          <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <PackageIcon className="h-6 w-6 text-brand-text" />
          </div>
          <h2 className="text-xl font-semibold">Sell lesson packs</h2>
          <p className="text-muted-foreground">
            Bundle lessons, track balances automatically, and stop chasing one-off invoices.
            Turning this on adds three starter templates you can edit or delete.
          </p>
          <Button size="lg" onClick={enablePackages}>
            Enable packages
          </Button>
        </Card>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Tabs defaultValue="sold">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <TabsList>
            <TabsTrigger value="sold">Sold packs</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
          </TabsList>
          <div className="flex gap-2 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => setTourOpen(true)}>
              <HelpCircle className="h-4 w-4 mr-2" /> How it works
            </Button>
            <Button variant="outline" onClick={() => setBulkOpen(true)}>
              <Repeat2 className="h-4 w-4 mr-2" /> Convert clients
            </Button>
            <Button variant="outline" asChild>
              <a href="/app/settings/policy">
                <Settings2 className="h-4 w-4 mr-2" />
                Cancellation policy
              </a>
            </Button>
            <Button onClick={() => setSellDialog(true)} disabled={templates.filter((t) => t.active).length === 0}>
              <Plus className="h-4 w-4 mr-2" /> Sell a pack
            </Button>
          </div>
        </div>

        <TabsContent value="sold" className="space-y-3">
          {packs.length === 0 ? (
            <PackagesEmptyState
              canSell={templates.filter((t) => t.active).length > 0}
              onSell={() => setSellDialog(true)}
              onTour={() => setTourOpen(true)}
            />
          ) : (
            packs.map((p) => {
              const client = clientById[p.coach_student_id];
              const tpl = p.template_id ? tplById[p.template_id] : null;
              const lowBalance =
                p.status === "active" &&
                p.lessons_remaining <= (tpl?.low_balance_threshold ?? 2) &&
                p.lessons_remaining > 0;
              return (
                <Card key={p.id} className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{client?.student_name ?? "Unknown client"}</span>
                        <Badge variant={STATUS_VARIANT[p.status]}>{STATUS_LABEL[p.status]}</Badge>
                        {lowBalance && (
                          <Badge variant="outline" className="border-amber-500 text-amber-600">
                            <AlertCircle className="h-3 w-3 mr-1" /> Low balance
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {tpl?.name ?? "Custom pack"} · {dollars(p.price_cents)}
                        {p.expires_at && ` · expires ${new Date(p.expires_at).toLocaleDateString()}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <div className="text-2xl font-bold tabular-nums">
                          {p.lessons_remaining}
                          <span className="text-base text-muted-foreground">/{p.lessons_total}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">lessons left</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setAdjustDialog(p)}>
                        <Minus className="h-4 w-4 mr-1" /> Adjust
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => setExtendDialog(p)}
                            disabled={p.status !== "active" && p.status !== "pending_payment"}
                          >
                            <CalendarPlus className="h-4 w-4 mr-2" /> Extend expiry
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setExpireDialog(p)}
                            disabled={p.status === "expired" || p.status === "void" || p.status === "refunded"}
                            className="text-destructive focus:text-destructive"
                          >
                            <CircleSlash className="h-4 w-4 mr-2" /> Force expire
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </TabsContent>


        <TabsContent value="templates" className="space-y-3">
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setTplDialog({ open: true })}>
              <Plus className="h-4 w-4 mr-2" /> New template
            </Button>
          </div>
          {templates.length === 0 ? (
            <Card className="p-8 text-center text-muted-foreground">No templates yet.</Card>
          ) : (
            templates.map((t) => (
              <Card key={t.id} className={`p-4 ${!t.active ? "opacity-60" : ""}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{t.name}</span>
                      {!t.active && <Badge variant="outline">Archived</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {t.lessons_count} lessons · {dollars(t.price_cents)}
                      {t.expiry_days && ` · expires in ${t.expiry_days} days`}
                    </div>
                    {t.description && (
                      <p className="text-sm text-muted-foreground mt-1">{t.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setTplDialog({ open: true, editing: t })}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleArchive(t)}>
                      {t.active ? <Archive className="h-4 w-4" /> : <ArchiveRestore className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      <TemplateDialog
        state={tplDialog}
        onClose={() => setTplDialog({ open: false })}
        onSaved={loadAll}
        coachId={user?.id}
      />

      <SellPackDialog
        open={sellDialog}
        onClose={() => setSellDialog(false)}
        onSaved={loadAll}
        coachId={user?.id}
        clients={clients}
        templates={templates.filter((t) => t.active)}
      />

      <AdjustBalanceDialog
        pack={adjustDialog}
        onClose={() => setAdjustDialog(null)}
        onSaved={loadAll}
        coachId={user?.id}
      />

      <ExtendExpiryDialog
        pack={extendDialog}
        onClose={() => setExtendDialog(null)}
        onSaved={loadAll}
        coachId={user?.id}
      />

      <ForceExpireDialog
        pack={expireDialog}
        onClose={() => setExpireDialog(null)}
        onSaved={loadAll}
        coachId={user?.id}
        clientName={expireDialog ? clientById[expireDialog.coach_student_id]?.student_name : undefined}
      />


      <PackagesConceptCarousel
        open={
          tourOpen ||
          (packagesEnabled === true && !onboardingDone && templates.length > 0)
        }
        onClose={() => {
          setTourOpen(false);
          if (!onboardingDone) setOnboardingDone(new Date().toISOString());
        }}
        onCompleted={loadAll}
        coachId={user?.id}
        templates={templates}
        initialSlide={1}
      />

      <BulkConversionWizard
        open={
          bulkOpen ||
          (packagesEnabled === true && !!onboardingDone && !bulkDone && clients.length > 0)
        }
        onClose={() => {
          setBulkOpen(false);
          if (!bulkDone && user?.id) {
            const ts = new Date().toISOString();
            setBulkDone(ts);
            void supabase
              .from("coach_profile")
              .update({ bulk_conversion_completed_at: ts })
              .eq("coach_id", user.id);
          }
        }}
        onCompleted={loadAll}
        coachId={user?.id}
        templates={templates}
      />
    </AppShell>
  );
}


function TemplateDialog({
  state,
  onClose,
  onSaved,
  coachId,
}: {
  state: { open: boolean; editing?: Template };
  onClose: () => void;
  onSaved: () => void;
  coachId?: string;
}) {
  const editing = state.editing;
  const [form, setForm] = useState({
    name: "",
    description: "",
    lessons_count: 5,
    price_dollars: 0,
    expiry_days: 180,
    low_balance_threshold: 2,
  });

  useEffect(() => {
    if (state.open) {
      setForm({
        name: editing?.name ?? "",
        description: editing?.description ?? "",
        lessons_count: editing?.lessons_count ?? 5,
        price_dollars: editing ? editing.price_cents / 100 : 0,
        expiry_days: editing?.expiry_days ?? 180,
        low_balance_threshold: editing?.low_balance_threshold ?? 2,
      });
    }
  }, [state.open, editing]);

  async function save() {
    if (!coachId) return;
    if (!form.name.trim()) return toast.error("Name required");
    if (form.lessons_count < 1) return toast.error("Lessons must be at least 1");

    const payload = {
      coach_id: coachId,
      name: form.name.trim(),
      description: form.description.trim() || null,
      lessons_count: Math.floor(form.lessons_count),
      price_cents: Math.round(form.price_dollars * 100),
      expiry_days: form.expiry_days || null,
      low_balance_threshold: form.low_balance_threshold,
    };

    const { error } = editing
      ? await supabase.from("package_templates").update(payload).eq("id", editing.id)
      : await supabase.from("package_templates").insert(payload);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Template updated." : "Template created.");
    onClose();
    onSaved();
  }

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit template" : "New template"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Lessons</Label>
              <Input
                type="number"
                min={1}
                value={form.lessons_count}
                onChange={(e) => setForm({ ...form, lessons_count: parseInt(e.target.value || "0", 10) })}
              />
            </div>
            <div>
              <Label>Price ($)</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={form.price_dollars}
                onChange={(e) => setForm({ ...form, price_dollars: parseFloat(e.target.value || "0") })}
              />
            </div>
            <div>
              <Label>Expires after (days)</Label>
              <Input
                type="number"
                min={0}
                value={form.expiry_days}
                onChange={(e) => setForm({ ...form, expiry_days: parseInt(e.target.value || "0", 10) })}
              />
            </div>
            <div>
              <Label>Low-balance alert at</Label>
              <Input
                type="number"
                min={0}
                value={form.low_balance_threshold}
                onChange={(e) =>
                  setForm({ ...form, low_balance_threshold: parseInt(e.target.value || "0", 10) })
                }
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save}>{editing ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SellPackDialog({
  open,
  onClose,
  onSaved,
  coachId,
  clients,
  templates,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  coachId?: string;
  clients: Client[];
  templates: Template[];
}) {
  const [clientId, setClientId] = useState<string>("");
  const [templateId, setTemplateId] = useState<string>("");
  const [openWithoutPayment, setOpenWithoutPayment] = useState(false);

  useEffect(() => {
    if (open) {
      setClientId("");
      setTemplateId("");
      setOpenWithoutPayment(false);
    }
  }, [open]);

  async function sell() {
    if (!coachId) return;
    if (!clientId || !templateId) return toast.error("Pick a client and a template");
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return;

    const now = new Date();
    const expires = tpl.expiry_days
      ? new Date(now.getTime() + tpl.expiry_days * 86400000).toISOString()
      : null;

    const ratePerLesson = tpl.lessons_count > 0 ? Math.round(tpl.price_cents / tpl.lessons_count) : 0;

    const { data: pkg, error } = await supabase
      .from("client_packages")
      .insert({
        coach_id: coachId,
        coach_student_id: clientId,
        template_id: templateId,
        status: openWithoutPayment ? "active" : "pending_payment",
        lessons_total: tpl.lessons_count,
        lessons_remaining: tpl.lessons_count,
        price_cents: tpl.price_cents,
        rate_at_purchase_cents: ratePerLesson,
        expires_at: expires,
        activated_at: openWithoutPayment ? now.toISOString() : null,
        opened_without_payment_at: openWithoutPayment ? now.toISOString() : null,
        opened_without_payment_by: openWithoutPayment ? coachId : null,
      })
      .select()
      .single();

    if (error || !pkg) return toast.error(error?.message ?? "Failed to sell pack");

    await supabase.from("package_ledger").insert({
      coach_id: coachId,
      client_package_id: pkg.id,
      coach_student_id: clientId,
      kind: "purchase",
      delta: tpl.lessons_count,
      balance_after: tpl.lessons_count,
      actor_id: coachId,
    });

    toast.success(openWithoutPayment ? "Pack opened. Remember to mark as paid." : "Pack created — awaiting payment.");
    onClose();
    onSaved();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Sell a pack</DialogTitle>
          <DialogDescription>Pick a client and a template.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Client</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger>
                <SelectValue placeholder="Pick a client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.student_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger>
                <SelectValue placeholder="Pick a template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} — {t.lessons_count} lessons · {dollars(t.price_cents)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={openWithoutPayment}
              onChange={(e) => setOpenWithoutPayment(e.target.checked)}
              className="mt-1"
            />
            <span>
              Open pack immediately (cash / Venmo already received).
              You'll get a reminder to record the payment.
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={sell}>Sell pack</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdjustBalanceDialog({
  pack,
  onClose,
  onSaved,
  coachId,
}: {
  pack: ClientPackage | null;
  onClose: () => void;
  onSaved: () => void;
  coachId?: string;
}) {
  const [delta, setDelta] = useState(-1);
  const [reason, setReason] = useState<string>("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (pack) {
      setDelta(-1);
      setReason("");
      setNote("");
    }
  }, [pack?.id]);

  if (!pack) return null;

  const newBalance = pack.lessons_remaining + delta;
  const valid = reason !== "" && delta !== 0 && newBalance >= 0;

  async function apply() {
    if (!coachId || !pack || !valid) return;
    const newBal = pack.lessons_remaining + delta;

    const { error: updErr } = await supabase
      .from("client_packages")
      .update({ lessons_remaining: newBal })
      .eq("id", pack.id);
    if (updErr) return toast.error(updErr.message);

    const { error: ledErr } = await supabase.from("package_ledger").insert({
      coach_id: coachId,
      client_package_id: pack.id,
      coach_student_id: pack.coach_student_id,
      kind: reason === "comp_lesson" ? "comp" : "manual_adjustment",
      delta,
      balance_after: newBal,
      reason: reason as "gift" | "correction" | "goodwill" | "comp_lesson" | "other",
      reason_note: note.trim() || null,
      actor_id: coachId,
    });
    if (ledErr) return toast.error(ledErr.message);
    toast.success("Balance adjusted.");
    onClose();
    onSaved();
  }

  return (
    <Dialog open={!!pack} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust balance</DialogTitle>
          <DialogDescription>
            Current: {pack.lessons_remaining} of {pack.lessons_total} lessons remaining.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Change by (±N lessons)</Label>
            <Input
              type="number"
              value={delta}
              onChange={(e) => setDelta(parseInt(e.target.value || "0", 10))}
            />
            <p className="text-xs text-muted-foreground mt-1">
              New balance: <strong>{newBalance}</strong> {newBalance < 0 && "(invalid — cannot go below 0)"}
            </p>
          </div>
          <div>
            <Label>Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Pick a reason" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gift">Gift</SelectItem>
                <SelectItem value="correction">Correction (fix a mistake)</SelectItem>
                <SelectItem value="goodwill">Goodwill</SelectItem>
                <SelectItem value="comp_lesson">Comp lesson</SelectItem>
                <SelectItem value="other">Other (note required)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Note (optional unless Other)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={apply}
            disabled={!valid || (reason === "other" && !note.trim())}
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExtendExpiryDialog({
  pack,
  onClose,
  onSaved,
  coachId,
}: {
  pack: ClientPackage | null;
  onClose: () => void;
  onSaved: () => void;
  coachId?: string;
}) {
  const [days, setDays] = useState(30);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (pack) {
      setDays(30);
      setNote("");
    }
  }, [pack?.id]);

  if (!pack) return null;

  const base = pack.expires_at ? new Date(pack.expires_at) : new Date();
  const newExpiry = new Date(base.getTime() + days * 86400000);
  const valid = days !== 0;

  async function apply() {
    if (!coachId || !pack || !valid) return;
    const { error: updErr } = await supabase
      .from("client_packages")
      .update({ expires_at: newExpiry.toISOString() })
      .eq("id", pack.id);
    if (updErr) return toast.error(updErr.message);

    const { error: ledErr } = await supabase.from("package_ledger").insert({
      coach_id: coachId,
      client_package_id: pack.id,
      coach_student_id: pack.coach_student_id,
      kind: "extend",
      delta: 0,
      balance_after: pack.lessons_remaining,
      reason_note:
        (note.trim() ? note.trim() + " · " : "") +
        `Expiry ${days >= 0 ? "extended" : "shortened"} by ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"}`,
      actor_id: coachId,
    });
    if (ledErr) return toast.error(ledErr.message);
    toast.success("Expiry updated.");
    onClose();
    onSaved();
  }

  return (
    <Dialog open={!!pack} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Extend expiry</DialogTitle>
          <DialogDescription>
            Current expiry:{" "}
            <strong>
              {pack.expires_at ? new Date(pack.expires_at).toLocaleDateString() : "never"}
            </strong>
            .
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Days to add (use negative to shorten)</Label>
            <Input
              type="number"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value || "0", 10))}
            />
            <p className="text-xs text-muted-foreground mt-1">
              New expiry: <strong>{newExpiry.toLocaleDateString()}</strong>
            </p>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={apply} disabled={!valid}>
            Update expiry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ForceExpireDialog({
  pack,
  onClose,
  onSaved,
  coachId,
  clientName,
}: {
  pack: ClientPackage | null;
  onClose: () => void;
  onSaved: () => void;
  coachId?: string;
  clientName?: string;
}) {
  const [note, setNote] = useState("");

  useEffect(() => {
    if (pack) setNote("");
  }, [pack?.id]);

  if (!pack) return null;

  async function apply() {
    if (!coachId || !pack) return;
    const remaining = pack.lessons_remaining;

    const { error: updErr } = await supabase
      .from("client_packages")
      .update({ status: "expired", lessons_remaining: 0 })
      .eq("id", pack.id);
    if (updErr) return toast.error(updErr.message);

    const { error: ledErr } = await supabase.from("package_ledger").insert({
      coach_id: coachId,
      client_package_id: pack.id,
      coach_student_id: pack.coach_student_id,
      kind: "expire",
      delta: -remaining,
      balance_after: 0,
      reason_note: note.trim() || "Force-expired by coach",
      actor_id: coachId,
    });
    if (ledErr) return toast.error(ledErr.message);
    toast.success("Pack expired.");
    onClose();
    onSaved();
  }

  return (
    <Dialog open={!!pack} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Force expire pack</DialogTitle>
          <DialogDescription>
            This ends the pack immediately for {clientName ?? "this client"}. The remaining{" "}
            <strong>{pack.lessons_remaining}</strong> lesson
            {pack.lessons_remaining === 1 ? "" : "s"} will be zeroed and logged. This action is
            reversible from the Activity tab.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label>Reason (optional)</Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={apply}>
            Expire pack
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
